import * as React from "react"

import { cn } from "../../cn"
import { Label, type LabelProps } from "../Label/Label"

export interface SettingRowProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  label: React.ReactNode
  tooltip?: React.ReactNode
  tooltipIcon?: LabelProps["tooltipIcon"]
  /** Renders the control under the label instead of beside it. */
  stacked?: boolean
  /** For a stacked row: something small to sit at the right of the label. */
  action?: React.ReactNode
  /** Shown under the row in the danger colour. */
  error?: React.ReactNode
  /**
   * The control. Given a render function, it receives the id to put on the
   * control so the label is associated with it.
   */
  children: React.ReactNode | ((controlId: string) => React.ReactNode)
}

/**
 * One setting in a settings list: label and help icon on the left, control on
 * the right. Every row shares this shape so a panel of toggles and selects
 * scans as settings rather than as a form.
 */
const SettingRow = React.forwardRef<HTMLDivElement, SettingRowProps>(
  (
    {
      label,
      tooltip,
      tooltipIcon,
      stacked,
      action,
      error,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const controlId = React.useId()
    const control =
      typeof children === "function" ? children(controlId) : children
    const labelNode = (
      <Label
        htmlFor={controlId}
        tooltip={
          tooltip ? <div className="max-w-xs">{tooltip}</div> : undefined
        }
        tooltipIcon={tooltipIcon}
      >
        {label}
      </Label>
    )

    if (stacked) {
      return (
        <div ref={ref} className={cn("space-y-2 py-2", className)} {...props}>
          <div className="flex min-h-8 items-center justify-between gap-6">
            {labelNode}
            {action}
          </div>
          {control}
          {error && <p className="text-xs text-coval-danger-text">{error}</p>}
        </div>
      )
    }

    return (
      <div ref={ref} className={cn("py-1", className)} {...props}>
        <div className="flex min-h-12 items-center justify-between gap-6">
          {labelNode}
          <div className="flex shrink-0 items-center gap-3">{control}</div>
        </div>
        {error && <p className="text-xs text-coval-danger-text">{error}</p>}
      </div>
    )
  },
)

SettingRow.displayName = "SettingRow"

export { SettingRow }
